import {
    finiteRegionNumber,
    regionColor,
    regionUniformShader,
    createRegionUniforms,
    writeRegionUniform,
    type RegionUniform,
} from './region-shape';
import { applyAreaShader, areaBounds, combineAreaShader, drawableAreas, type AreaApply, type AreaShape, type ResolvedArea } from './area-shape';
import { outlineSlabShader } from './outline-shape';
import { splatObjectLaneCall } from './splat-objects';
import type { RegionColorEffect } from './splat-region-effects';
import type { SceneTrigger } from '../types';

/**
 * A haze region: a sphere, box, outline or whole splat objects (the shared shape picker, see area-shape.ts). `apply`
 * says where the haze goes: 'inside' hazes the shape, 'outside' keeps the shape clear and hazes
 * everything else. Unset, it follows the config's `mode`.
 */
export interface HazeRegion extends AreaShape {
    id: string;
    name: string;
    enabled: boolean;
    /**
     * Optional runtime switches (same kinds as overlay triggers). Without triggers the region
     * is always on; with them it applies only while a trigger holds or after an event fires,
     * and a hotspot click toggles it. The editor honours them too, except for the region being edited.
     */
    triggers?: SceneTrigger[];
}
export interface HazeConfig {
    enabled: boolean;
    regions: HazeRegion[];
    showBounds?: boolean;
    /**
     * Where regions without their own `apply` put the haze: 'clear-inside' (default) keeps them
     * clear and hazes everything else; 'fog-inside' hazes only them. Scenes saved before regions
     * had their own `apply` use this for every region.
     */
    mode?: 'clear-inside' | 'fog-inside';
    color?: string;
    amount?: number;
}
export const MAX_HAZE_REGIONS = 4;
/** The `apply` a haze region without its own gets from the config's `mode`. */
export const hazeDefaultApply = (mode: HazeConfig['mode'] | undefined): AreaApply => (mode === 'fog-inside' ? 'inside' : 'outside');
/** The regions the effect draws, in uniform slot order (enabled, drawable, at most MAX_HAZE_REGIONS). */
export function drawableHazeRegions<T extends { enabled?: boolean }>(regions: ReadonlyArray<T | null | undefined> | undefined, mode?: HazeConfig['mode']) {
    return drawableAreas<T>(regions, MAX_HAZE_REGIONS, hazeDefaultApply(mode));
}
export interface ResolvedHazeRegion {
    id: string;
    name: string;
    enabled: true;
    area: ResolvedArea;
    triggers?: SceneTrigger[];
}
export function resolveHazeConfig(config: HazeConfig | undefined) {
    if (!config?.enabled) return undefined;
    const regions: ResolvedHazeRegion[] = drawableHazeRegions<HazeRegion>(config.regions, config.mode).map(({ item: r, area }) => ({
        id: r.id,
        name: r.name,
        enabled: true,
        area,
        ...(Array.isArray(r.triggers) ? { triggers: r.triggers } : {}),
    }));
    if (!regions.length) return undefined;
    return {
        regions,
        showBounds: config.showBounds !== false,
        mode: config.mode === 'fog-inside' ? ('fog-inside' as const) : ('clear-inside' as const),
        color: regionColor(config.color, '#b8c4d0'),
        amount: finiteRegionNumber(config.amount, 0.85, 0, 1),
    };
}
/** Haze color (rgb) and amount (a) ride in a uniform, so they change without recompiling. */
export const HAZE_UNIFORM = 'uHaze';
export interface HazeLook {
    color: { r: number; g: number; b: number };
    amount: number;
}
/** The configured look (defaults applied), or the defaults when haze is off. */
export function resolveHazeLook(config: HazeConfig | undefined): HazeLook {
    const hex = regionColor(config?.color, '#b8c4d0');
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
    return { color: { r, g, b }, amount: finiteRegionNumber(config?.amount, 0.85, 0, 1) };
}
export function writeHazeUniform(uniform: Float32Array, look: HazeLook): void {
    uniform[0] = look.color.r;
    uniform[1] = look.color.g;
    uniform[2] = look.color.b;
    uniform[3] = look.amount;
}
export const hazeRegionUniform = (index: number) => `uHazeRegion${index}`;
export const createHazeRegionUniforms = (): RegionUniform[] => createRegionUniforms(MAX_HAZE_REGIONS);
/**
 * @param uniform Array the caller keeps to drive the look at runtime; created when omitted.
 *   It is (re)filled with the configured look.
 */
export function buildHazeEffect(config: HazeConfig | undefined, uniform?: Float32Array, regionUniforms?: RegionUniform[]): RegionColorEffect | undefined {
    const cfg = resolveHazeConfig(config);
    if (!cfg) return undefined;
    const target = uniform ?? new Float32Array(4);
    const regionTargets = regionUniforms ?? createHazeRegionUniforms();
    writeHazeUniform(target, resolveHazeLook(config));
    cfg.regions.forEach((r, i) => {
        if (r.area.kind === 'sphere' || r.area.kind === 'box') writeRegionUniform(regionTargets[i], r.area.region);
    });
    const applies = cfg.regions.map((r) => r.area.apply);
    // Splat object regions: lane k is the k-th of them (splat-objects.ts).
    const objectSets: string[][] = [];
    const lanes = cfg.regions.map((r) => (r.area.kind === 'splats' ? objectSets.push(r.area.ids) - 1 : -1));
    const shader = (lang: 'glsl' | 'wgsl') => {
        // Sphere / box: transform + feather from uniforms. Outline: baked into the source. Splat
        // objects: the splat's object flags.
        const weights = cfg.regions
            .map((r, i) =>
                r.area.kind === 'outline'
                    ? `${outlineSlabShader(r.area.slab, 'hazeWeight' + i, lang)}\n`
                    : r.area.kind === 'splats'
                      ? `${lang === 'glsl' ? `float hazeWeight${i}(vec3 center) { return ${splatObjectLaneCall('haze', lanes[i])}; }` : `fn hazeWeight${i}(center: vec3f) -> f32 { return ${splatObjectLaneCall('haze', lanes[i])}; }`}\n`
                      : regionUniformShader(r.area.kind, 'hazeWeight' + i, hazeRegionUniform(i), lang),
            )
            .join('\n');
        // How much haze each point gets: 'outside' regions (the classic "keep this clear") each
        // keep their own area clear, 'inside' regions add haze where they are.
        const amount = combineAreaShader(cfg.regions.map((_, i) => applyAreaShader(`hazeWeight${i}(center)`, applies[i])), applies);
        const statement =
            lang === 'glsl'
                ? `color.rgb = mix(color.rgb, ${HAZE_UNIFORM}.rgb, hazed * ${HAZE_UNIFORM}.a);`
                : `*color = vec4f(mix((*color).rgb, uniform.${HAZE_UNIFORM}.rgb, hazed * uniform.${HAZE_UNIFORM}.a), (*color).a);`;
        return lang === 'glsl'
            ? `uniform vec4 ${HAZE_UNIFORM};
${weights}
void region_haze(vec3 center, inout vec4 color) { float hazed = ${amount}; ${statement} }`
            : `uniform ${HAZE_UNIFORM}: vec4f;
${weights}
fn region_haze(center: vec3f, color: ptr<function, vec4f>) { let hazed = ${amount}; ${statement} }`;
    };
    return {
        key: 'haze',
        bypassFog: true,
        glsl: shader('glsl'),
        wgsl: shader('wgsl'),
        bounds: cfg.showBounds ? cfg.regions.map((r) => areaBounds(r.area)) : [],
        uniforms: {
            [HAZE_UNIFORM]: target,
            ...Object.fromEntries(cfg.regions.flatMap((r, i) => r.area.kind === 'sphere' || r.area.kind === 'box' ? [
                [hazeRegionUniform(i), regionTargets[i].matrix],
                [`${hazeRegionUniform(i)}P`, regionTargets[i].params],
            ] : [])),
        },
        ...(objectSets.length ? { objectSets } : {}),
    };
}
